// @ts-strict-ignore
import React, { type CSSProperties, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import * as monthUtils from 'loot-core/src/shared/months';

import { SvgArrowButtonLeft1, SvgArrowButtonRight1, SvgCalendar } from '../../icons';
import { cn } from '../../lib/utils';

import { type MonthBounds } from './MonthsContext';

type MonthPickerProps = {
  startMonth: string;
  numDisplayed: number;
  monthBounds: MonthBounds;
  sparseMonths?: string[];
  activeMonth?: string;
  style: CSSProperties;
  onSelect: (month: string) => void;
};

export const MonthPicker = ({
  startMonth,
  numDisplayed,
  monthBounds,
  sparseMonths,
  activeMonth,
  onSelect,
}: MonthPickerProps) => {
  const { t } = useTranslation();
  const stripRef = useRef<HTMLDivElement>(null);

  const currentMonth = monthUtils.currentMonth();
  const lastSelectedMonth = sparseMonths
    ? (() => {
        const idx = sparseMonths.indexOf(startMonth);
        return idx >= 0 ? sparseMonths[Math.min(idx + numDisplayed - 1, sparseMonths.length - 1)] : startMonth;
      })()
    : monthUtils.addMonths(startMonth, numDisplayed - 1);
  // In sparse mode: use the explicitly tracked activeMonth if provided (preserves the
  // user's actual click target even when the window is clamped at the edges).
  const centerMonth = sparseMonths
    ? (activeMonth ?? (() => {
        const idx = sparseMonths.indexOf(startMonth);
        return idx >= 0 ? sparseMonths[Math.min(idx + Math.floor(numDisplayed / 2), sparseMonths.length - 1)] : startMonth;
      })())
    : monthUtils.addMonths(startMonth, Math.floor((numDisplayed - 1) / 2));

  // Build the list of months to render
  const PADDING = 12;
  const allMonths = sparseMonths ?? monthUtils.rangeInclusive(
    monthUtils.subMonths(monthBounds.start, PADDING),
    monthUtils.addMonths(monthBounds.end, PADDING),
  );

  // Group by year for year labels
  const yearGroups: { year: string; months: string[] }[] = [];
  for (const month of allMonths) {
    const year = monthUtils.getYear(month);
    const last = yearGroups[yearGroups.length - 1];
    if (!last || last.year !== year) {
      yearGroups.push({ year, months: [month] });
    } else {
      last.months.push(month);
    }
  }

  function scrollStrip(dir: -1 | 1) {
    stripRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
  }

  function scrollToMonth(monthId: string) {
    const strip = stripRef.current;
    const pill = document.getElementById(`month-pill-${monthId}`);
    if (!strip || !pill) return;
    // Calculate the pill's center relative to the strip scroll container
    const pillLeft = pill.offsetLeft - strip.offsetLeft;
    const pillCenter = pillLeft + pill.offsetWidth / 2;
    const targetScroll = pillCenter - strip.clientWidth / 2;
    strip.scrollTo({ left: targetScroll, behavior: 'smooth' });
  }

  function jumpToday() {
    onSelect(currentMonth);
    setTimeout(() => scrollToMonth(currentMonth), 50);
  }

  // Auto-scroll to the active/selected month when it changes
  useEffect(() => {
    scrollToMonth(activeMonth ?? startMonth);
  }, [startMonth, activeMonth]);

  return (
    <div className="flex flex-col min-w-0 w-full">
      {/* Controls row */}
      <div className="flex items-center gap-2 pb-2">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <SvgCalendar style={{ width: 14, height: 14 }} />
          <span className="text-xs">Timeline</span>
        </div>
        <button
          onClick={jumpToday}
          title={t('Today')}
          className="ml-auto flex items-center gap-1 bg-card border border-border rounded-md px-2.5 py-1 text-foreground text-xs hover:bg-accent hover:text-accent-foreground transition-colors"
        >
          Today
        </button>
        <button
          onClick={() => scrollStrip(-1)}
          title={t('Scroll left')}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <SvgArrowButtonLeft1 style={{ width: 16, height: 16 }} />
        </button>
        <button
          onClick={() => scrollStrip(1)}
          title={t('Scroll right')}
          className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          <SvgArrowButtonRight1 style={{ width: 16, height: 16 }} />
        </button>
      </div>

      {/* Scrollable strip */}
      <div
        ref={stripRef}
        className="overflow-x-auto min-w-0"
        style={{ scrollbarWidth: 'none' }}
      >
        <div className="flex items-end gap-0 min-w-max pb-1">
          {yearGroups.map(({ year, months }) => (
            <div key={year} className="flex flex-col">
              <span className="text-[10px] text-muted-foreground ml-1 mb-1 select-none font-bold">
                {year}
              </span>
              <div className="flex gap-0.5">
                {months.map(month => {
                  const isSelected = month >= startMonth && month <= lastSelectedMonth;
                  const isCenter = month === centerMonth;
                  const isCurrent = month === currentMonth;
                  const isBudgeted = sparseMonths
                    ? true
                    : month >= monthBounds.start && month <= monthBounds.end;
                  const isPast = month < startMonth;

                  return (
                    <button
                      id={`month-pill-${month}`}
                      key={month}
                      onClick={() => onSelect(month)}
                      className={cn(
                        'relative px-2.5 py-1 rounded-md text-xs transition-all duration-150 select-none cursor-pointer',
                        !isBudgeted
                          ? 'text-muted-foreground opacity-60'
                          : isCenter && isCurrent
                          ? 'bg-primary text-primary-foreground shadow-md scale-105 ring-2 ring-primary/30'
                          : isCenter
                          ? 'bg-primary text-primary-foreground shadow-md scale-105'
                          : isSelected && isCurrent
                          ? 'bg-primary/50 text-primary-foreground ring-1 ring-white/20'
                          : isSelected
                          ? 'bg-primary/50 text-primary-foreground'
                          : isCurrent
                          ? 'bg-accent text-accent-foreground ring-1 ring-primary/60 hover:bg-accent'
                          : isPast
                          ? 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
                          : 'text-foreground hover:bg-accent hover:text-accent-foreground',
                      )}
                    >
                      {monthUtils.format(month, 'MMM')}
                      {isCurrent && !isSelected && (
                        <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />
                      )}
                      {isCurrent && isSelected && (
                        <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white/60" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
